import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { GetAll_stage_Data } from '../../Redux/Actions/Stage_Action';
import { GetAllchannalData } from '../../Redux/Actions/ChannalActions';
import { GetCommunicationwayData } from '../../Redux/Actions/CommunicationWay_Action';
import { GetAllCampainData } from '../../Redux/Actions/Campaign_Actions';
import { GetallProjectsdata } from '../../Redux/Actions/Projects_Action';
import { Get_All_Sales_Action } from '../../Redux/Actions/SalesAction';
import { Notify } from '../../Utility/Notify';
import { db_url } from '../../Api/BaseUrl';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconPF       = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.5 8.5h3.5a2 2 0 1 1 0 4H9.5v4"/></svg>;
const IconChevron  = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>;
const IconSearch   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconClose    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconCheck    = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>;
const IconPlus     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconTrash    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>;
const IconEdit     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconEye      = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const IconRefresh  = ({ spinning }) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: spinning ? 'spin 1s linear infinite' : 'none' }}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.36-3.36L23 10M1 14l5.13 4.36A9 9 0 0020.49 15"/></svg>;
const IconLayers   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
const IconLink     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const inputSt = (isDark) => ({
  width: '100%', padding: '11px 14px', borderRadius: '10px', fontSize: '13px',
  border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`, backgroundColor: isDark ? '#151827' : '#f8fafc',
  color: isDark ? '#f1f5f9' : '#0f172a', fontFamily: "'DM Sans',sans-serif", outline: 'none', boxSizing: 'border-box',
});
const labelSt = (isDark) => ({
  display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: isDark ? '#6b7280' : '#64748b', marginBottom: '7px', fontFamily: "'DM Sans',sans-serif",
});

/* ─── Helper: find an option by its display name (case-insensitive) ────────
   Used to auto-lock "Stage" to "No Stage" and "Campaign" to "Default"
   without showing a picker to the user. */
const findByName = (list, name, key = 'name') =>
  (list || []).find(o => (o?.[key] || '').trim().toLowerCase() === name.trim().toLowerCase());

/* ─── Helper: pick whichever field actually holds the PF listing URL ────── */
const getListingUrl = (listing) =>
  listing?.url || listing?.pfUrl || listing?.listingUrl || listing?.link || listing?.permalink || '';

/* ─── Helper: match a Sales record to the logged-in user ────────────────────
   IMPORTANT: we must compare the logged-in user's id against the sales
   record's linked user (s.userlog._id / s.userlog.email) — NOT against the
   sales record's own _id. Those are two different ids. This mirrors the
   logic used in AddClients.js (getSalesOptions). */
const findOwnSalesRecord = (salesData, user) => {
  if (!user?.data) return null;
  return (salesData || []).find(
    s =>
      (s?.userlog?._id && s.userlog._id === user.data._id) ||
      (s?.userlog?.email && s.userlog.email === user.data.email)
  );
};

/* ─── Helper: pull the `salesdata` id back out of a mapping's stored
   targetWebhookUrl (it's saved as a query param, not a plain field) ─────── */
const getSalesIdFromMapping = (m) => {
  if (!m?.targetWebhookUrl) return '';
  const qIndex = m.targetWebhookUrl.indexOf('?');
  if (qIndex === -1) return '';
  try {
    const params = new URLSearchParams(m.targetWebhookUrl.slice(qIndex + 1));
    return params.get('salesdata') || '';
  } catch {
    return '';
  }
};

/* ─── Searchable Single Select ───────────────────────────────────────────── */
const SingleSelect = ({ label, options, value, onChange, isDark, getLabel, getId, required, disabled }) => {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const ref        = useRef(null);
  const selected   = options.find(o => getId(o) === value);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest('[data-singleselect-portal]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Keep the dropdown pinned to the trigger while open (scroll/resize inside drawers etc.)
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const above = spaceBelow < 240 && rect.top > spaceBelow;
      setDropPos({ left: rect.left, width: rect.width, top: above ? rect.top - 244 : rect.bottom + 4 });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const above = spaceBelow < 240 && rect.top > spaceBelow;
      setDropPos({ left: rect.left, width: rect.width, top: above ? rect.top - 244 : rect.bottom + 4 });
    }
    setOpen(o => !o);
    setSearch('');
  };

  const filtered = options.filter(o => getLabel(o).toLowerCase().includes(search.toLowerCase()));
  const hasError = required && !value;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div ref={triggerRef} onClick={handleOpen} style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', fontSize: '13px', border: `1.5px solid ${hasError ? '#ef4444' : open ? '#2563eb' : (isDark ? '#2d3248' : '#e2e8f0')}`, backgroundColor: disabled ? (isDark ? '#1a1d2b' : '#f1f5f9') : (isDark ? '#151827' : '#f8fafc'), color: selected ? (isDark ? '#f1f5f9' : '#0f172a') : (isDark ? '#6b7280' : '#94a3b8'), fontFamily: "'DM Sans',sans-serif", cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box', userSelect: 'none', minHeight: '44px', boxShadow: open ? '0 0 0 3px rgba(37,99,235,0.12)' : (hasError ? '0 0 0 3px rgba(239,68,68,0.1)' : 'none'), transition: 'all 0.15s', opacity: disabled ? 0.75 : 1 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8, fontSize: '13px' }}>{selected ? getLabel(selected) : label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {!disabled && value && <span onClick={(e) => { e.stopPropagation(); onChange(''); }} style={{ display: 'flex', color: isDark ? '#6b7280' : '#94a3b8', padding: 2 }}><IconClose /></span>}
          {!disabled && <span style={{ display: 'flex', color: isDark ? '#6b7280' : '#94a3b8', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><IconChevron /></span>}
        </span>
      </div>
      {open && createPortal(
        <div
          data-singleselect-portal
          style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 999999, backgroundColor: isDark ? '#1e2132' : '#fff', border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`, borderRadius: '12px', overflow: 'hidden', boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.55)' : '0 8px 32px rgba(0,0,0,0.12)' }}
        >
          <div style={{ position: 'relative', borderBottom: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}` }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: isDark ? '#6b7280' : '#94a3b8', display: 'flex', pointerEvents: 'none' }}><IconSearch /></span>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" style={{ width: '100%', padding: '10px 12px 10px 32px', fontSize: '13px', border: 'none', outline: 'none', backgroundColor: isDark ? '#151827' : '#f8fafc', color: isDark ? '#e5e7eb' : '#1f2937', fontFamily: "'DM Sans',sans-serif", boxSizing: 'border-box' }} />
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filtered.length === 0
              ? <div style={{ padding: '14px', fontSize: '13px', color: isDark ? '#6b7280' : '#94a3b8', textAlign: 'center' }}>No results</div>
              : filtered.map(o => {
                const id = getId(o); const isSel = value === id;
                return (
                  <div key={id} onClick={() => { onChange(id); setOpen(false); }} style={{ padding: '10px 14px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, backgroundColor: isSel ? (isDark ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.06)') : 'transparent', fontFamily: "'DM Sans',sans-serif", borderLeft: isSel ? '3px solid #1d4ed8' : '3px solid transparent' }}
                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'; }}
                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {isSel && <span style={{ color: '#1d4ed8', display: 'flex', flexShrink: 0 }}><IconCheck /></span>}
                    <span style={{ color: isDark ? '#e5e7eb' : '#1f2937' }}>{getLabel(o)}</span>
                  </div>
                );
              })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

/* ─── Listing Details Modal ──────────────────────────────────────────────── */
const DetailRow = ({ label, value, isDark }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f1f5f9'}` }}>
      <span style={{ fontSize: 12, color: isDark ? '#6b7280' : '#94a3b8', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: isDark ? '#e5e7eb' : '#1f2937', textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
};

const ListingDetailsModal = ({ listing, onClose, isDark }) => {
  if (!listing) return null;
  const listingUrl = getListingUrl(listing);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 640, maxHeight: '86vh', overflowY: 'auto', backgroundColor: isDark ? '#1e2132' : '#fff', borderRadius: 16, border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>

        {listing.images?.[0] && (
          <div style={{ height: 220, background: `url(${listing.images[0]}) center/cover`, borderRadius: '16px 16px 0 0' }} />
        )}

        <div style={{ padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>
              {listing.titleEn || listing.reference}
            </div>
            <span onClick={onClose} style={{ cursor: 'pointer', color: isDark ? '#6b7280' : '#94a3b8', display: 'flex', flexShrink: 0 }}><IconClose /></span>
          </div>
          <div style={{ fontSize: 12, color: isDark ? '#6b7280' : '#94a3b8', marginBottom: 16 }}>{listing.reference}</div>

          {/* Status badges */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: listing.state === 'live' ? '#16a34a22' : '#94a3b822', color: listing.state === 'live' ? '#16a34a' : '#94a3b8' }}>
              {listing.state === 'live' ? '✓ Live' : listing.state}
            </span>
            {listing.isLiveOnPortal && (
              <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: '#1d4ed822', color: '#1d4ed8' }}>Published on Portal</span>
            )}
            {listing.qualityScoreValue > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: '#f59e0b22', color: '#f59e0b' }}>Quality {listing.qualityScoreValue}%</span>
            )}
          </div>

          {/* Important fields */}
          <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#6b7280' : '#94a3b8', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.08em' }}>
            Key Details
          </div>
          <div style={{ marginBottom: 18 }}>
            <DetailRow label="Price" value={listing.priceAmount ? `${listing.priceAmount.toLocaleString()} AED (${listing.priceType})` : ''} isDark={isDark} />
            <DetailRow label="Property Type" value={listing.propertyType} isDark={isDark} />
            <DetailRow label="Category" value={listing.category} isDark={isDark} />
            <DetailRow label="Bedrooms" value={listing.bedrooms} isDark={isDark} />
            <DetailRow label="Bathrooms" value={listing.bathrooms} isDark={isDark} />
            <DetailRow label="Size" value={listing.size ? `${listing.size} sqft` : ''} isDark={isDark} />
            <DetailRow label="Plot Size" value={listing.plotSize ? `${listing.plotSize} sqft` : ''} isDark={isDark} />
            <DetailRow label="Location" value={listing.locationName} isDark={isDark} />
            <DetailRow label="Agent" value={listing.agentName} isDark={isDark} />
          </div>

          {/* Secondary / less important fields */}
          <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#6b7280' : '#94a3b8', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.08em' }}>
            Additional Info
          </div>
          <div style={{ marginBottom: 18 }}>
            <DetailRow label="Unit Number" value={listing.unitNumber} isDark={isDark} />
            <DetailRow label="Furnishing" value={listing.furnishingType} isDark={isDark} />
            <DetailRow label="Project Status" value={listing.projectStatus} isDark={isDark} />
            <DetailRow label="Emirate" value={listing.uaeEmirate} isDark={isDark} />
            <DetailRow label="Published At" value={listing.publishedAt ? new Date(listing.publishedAt).toLocaleDateString() : ''} isDark={isDark} />
            <DetailRow label="Last Synced" value={listing.lastSyncedAt ? new Date(listing.lastSyncedAt).toLocaleString() : ''} isDark={isDark} />
          </div>

          {listing.amenities?.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#6b7280' : '#94a3b8', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.08em' }}>
                Amenities
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
                {listing.amenities.map((a, i) => (
                  <span key={i} style={{ padding: '4px 9px', borderRadius: 7, fontSize: 11, background: isDark ? '#151827' : '#f1f5f9', color: isDark ? '#9ca3af' : '#475569' }}>{a}</span>
                ))}
              </div>
            </>
          )}

          {listing.descriptionEn && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#6b7280' : '#94a3b8', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.08em' }}>
                Description
              </div>
              <p style={{ fontSize: 12.5, color: isDark ? '#9ca3af' : '#475569', lineHeight: 1.7, whiteSpace: 'pre-line', margin: 0 }}>
                {listing.descriptionEn}
              </p>
            </>
          )}

          {listingUrl && (
            <a
              href={listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 18, fontSize: 12.5, fontWeight: 700, color: '#1d4ed8', textDecoration: 'none' }}
            >
              <IconLink /> Open on Property Finder
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Right-side Drawer wrapper ──────────────────────────────────────────── */
const Drawer = ({ open, onClose, title, isDark, children, width = 720 }) => {
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 199998, opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none', transition: 'opacity 0.25s ease' }}
      />
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: `min(${width}px, 100vw)`, zIndex: 199999,
          backgroundColor: isDark ? '#1e2132' : '#fff', boxShadow: '-16px 0 48px rgba(0,0,0,0.25)',
          transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`, flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>{title}</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: isDark ? '#6b7280' : '#94a3b8', display: 'flex' }}><IconClose /></span>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 22 }}>
          {children}
        </div>
      </div>
    </>
  );
};

/* ─── Bulk Assign Panel (content used inside the Drawer) ────────────────── */
const BulkAssignPanel = ({ isDark, Allprojects, salesData, allStages, allChannal, allCW, allCampaigns, user, onDone }) => {
  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewingListing, setViewingListing] = useState(null);

  const [project, setProject] = useState('');
  const [salesdata, setSalesdata] = useState('');
  const [stage, setStage] = useState('');
  const [channal, setChannal] = useState('');
  const [communicationway, setCommunicationway] = useState('');
  const [campaignid, setCampaignid] = useState('');
  const [note, setNote] = useState('');

  // ✅ Stage اتقفل تلقائي على "No Stage" — مفيش اختيار يدوي
  useEffect(() => {
    const noStage = findByName(allStages, 'No Stage');
    if (noStage) setStage(noStage._id);
  }, [allStages]);

  // ✅ Campaign اتقفل تلقائي على "Default" — مفيش اختيار يدوي
  useEffect(() => {
    const defaultCampaign = findByName(allCampaigns, 'Default', 'CampainName');
    if (defaultCampaign) setCampaignid(defaultCampaign._id);
  }, [allCampaigns]);

  // ✅ لو المستخدم مش Admin ولا Marketer، يتقفل الـ Sales عليه هو بس (disabled)
  // ✅ FIX: بنقارن userlog بتاع الـ sales record باليوزر الحالي، مش s._id
  const userRole      = user?.data?.role;
  const salesRestricted = Boolean(userRole) && userRole !== 'Admin' && userRole !== 'Marketer';
  useEffect(() => {
    if (salesRestricted) {
      const own = findOwnSalesRecord(salesData, user);
      if (own) setSalesdata(own._id);
    }
  }, [salesRestricted, salesData, user]);

  const fetchListings = async () => {
    setLoadingListings(true);
    try {
      const { data } = await axios.get(`${db_url}api/v1/pf-listings`);
      setListings(data.data || []);
    } catch {
      Notify('error', 'Failed to load listings', 'error');
    } finally {
      setLoadingListings(false);
    }
  };

  useEffect(() => { fetchListings(); }, []);

  // ✅ زرار Sync Now — يعمل مزامنة فورية من PF ثم يملأ الـ dropdown تلقائي
  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${db_url}api/v1/pf-listings/sync-now`);
      Notify('success', data.message || 'Sync complete', 'success');
      await fetchListings(); // إعادة تحميل القائمة فورًا بعد المزامنة
    } catch (err) {
      Notify('error', err.response?.data?.message || 'Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = listings
    .filter(l => l.state === 'live') // إخفاء الإعلانات المنزّلة تلقائيًا
    .filter(l =>
      l.reference?.toLowerCase().includes(search.toLowerCase()) ||
      l.locationName?.toLowerCase().includes(search.toLowerCase()) ||
      l.titleEn?.toLowerCase().includes(search.toLowerCase())
    );

  const toggleRef = (ref) => {
    setSelectedRefs(prev => {
      const next = new Set(prev);
      next.has(ref) ? next.delete(ref) : next.add(ref);
      return next;
    });
  };

  const selectAllFiltered = () => setSelectedRefs(new Set(filtered.map(l => l.reference)));
  const clearSelection = () => setSelectedRefs(new Set());

  const handleBulkAssign = async () => {
    if (selectedRefs.size === 0) return Notify('error', 'اختار إعلان واحد على الأقل', 'error');
    if (!project || !salesdata || !stage || !channal || !communicationway || !campaignid)
      return Notify('error', 'كمّل كل الحقول المطلوبة', 'error');

    const params = {
      addedby: user?.data?._id || '', stage, Channal: channal, project, salesdata,
      Communicationway: communicationway, campaignid,
      includePhone2: 'false', includeWhatsapp: 'false', includeJobDesc: 'false',
      includeQuestion1: 'false', includeQuestion2: 'false', includeQuestion3: 'false',
      includeQuestion4: 'false', includeQuestion5: 'false',
    };
    const targetWebhookUrl = `${db_url}api/v1/users/create-with-params?${new URLSearchParams(params).toString()}`;

    setSaving(true);
    try {
      const { data } = await axios.post(`${db_url}api/v1/pf-mapping/bulk-assign`, {
        listingReferences: Array.from(selectedRefs),
        targetWebhookUrl,
        note,
      });
      Notify('success', data.message, 'success');
      setSelectedRefs(new Set());
      onDone?.();
    } catch (err) {
      Notify('error', err.response?.data?.message || 'فشلت العملية', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: 12, color: isDark ? '#6b7280' : '#94a3b8', marginBottom: 18 }}>
        Choose the CRM settings once, select multiple listings below, then assign them all in one click.
      </p>

      {/* Shared settings — Stage & Campaign اتشالوا من هنا وبيتحطوا تلقائي (No Stage / Default) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div><label style={labelSt(isDark)}>Project *</label><SingleSelect label="Choose project…" options={Allprojects} value={project} onChange={setProject} isDark={isDark} getLabel={o => o.name || ''} getId={o => o._id} required /></div>
        <div><label style={labelSt(isDark)}>Sales *</label><SingleSelect label="Choose sales…" options={salesData} value={salesdata} onChange={setSalesdata} isDark={isDark} getLabel={o => o.name || ''} getId={o => o._id} required disabled={salesRestricted} /></div>
        <div><label style={labelSt(isDark)}>Channel *</label><SingleSelect label="Choose channel…" options={allChannal} value={channal} onChange={setChannal} isDark={isDark} getLabel={o => o.name || ''} getId={o => o._id} required /></div>
        <div><label style={labelSt(isDark)}>Communication Way *</label><SingleSelect label="Choose way…" options={allCW} value={communicationway} onChange={setCommunicationway} isDark={isDark} getLabel={o => o.name || ''} getId={o => o._id} required /></div>
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={labelSt(isDark)}>Note</label>
        <input style={inputSt(isDark)} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Sharjah listings batch" />
      </div>

      {/* Listings checklist header + Sync Now button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '18px 0 10px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#6b7280' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Select Listings ({selectedRefs.size} selected / {filtered.length} live)
        </div>
        <button
          onClick={handleSyncNow}
          disabled={syncing}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`, background: 'transparent', color: isDark ? '#93c5fd' : '#1d4ed8', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}
        >
          <IconRefresh spinning={syncing} /> {syncing ? 'Syncing…' : 'Sync Now'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input style={{ ...inputSt(isDark), flex: 1 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by reference, title or location…" />
        <button onClick={selectAllFiltered} style={{ padding: '10px 14px', borderRadius: 9, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Select All</button>
        <button onClick={clearSelection} style={{ padding: '10px 14px', borderRadius: 9, border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`, background: 'transparent', color: isDark ? '#9ca3af' : '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Clear</button>
      </div>

      <div style={{ maxHeight: 380, overflowY: 'auto', border: `1.5px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`, borderRadius: 10 }}>
        {loadingListings ? (
          <p style={{ padding: 16, fontSize: 13, color: isDark ? '#6b7280' : '#94a3b8' }}>Loading listings…</p>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: isDark ? '#6b7280' : '#94a3b8', marginBottom: 10 }}>No listings found.</p>
            <button onClick={handleSyncNow} disabled={syncing} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {syncing ? 'Syncing…' : 'Run Sync Now'}
            </button>
          </div>
        ) : (
          filtered.map(l => {
            const listingUrl = getListingUrl(l);
            return (
              <div
                key={l.pfListingId}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${isDark ? '#2a2f45' : '#f1f5f9'}`, backgroundColor: selectedRefs.has(l.reference) ? (isDark ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.06)') : 'transparent' }}
              >
                <div onClick={() => toggleRef(l.reference)} style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${selectedRefs.has(l.reference) ? '#1d4ed8' : (isDark ? '#4b5563' : '#d1d5db')}`, background: selectedRefs.has(l.reference) ? '#1d4ed8' : 'transparent', flexShrink: 0, cursor: 'pointer' }} />
                <div onClick={() => toggleRef(l.reference)} style={{ minWidth: 0, flex: 1, cursor: 'pointer' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.titleEn || l.reference}
                  </div>
                  <div style={{ fontSize: 11, color: isDark ? '#6b7280' : '#94a3b8' }}>{l.propertyType} · {l.priceType} · {l.locationName}</div>
                </div>
                {/* ✅ زرار العين - يفتح تفاصيل الإعلان كاملة */}
                <span
                  onClick={(e) => { e.stopPropagation(); setViewingListing(l); }}
                  style={{ display: 'flex', color: isDark ? '#93c5fd' : '#1d4ed8', cursor: 'pointer', padding: 6, flexShrink: 0 }}
                  title="View details"
                >
                  <IconEye />
                </span>
                {/* ✅ لينك مباشر لصفحة الإعلان على Property Finder */}
                {listingUrl && (
                  <a
                    href={listingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: 'flex', color: isDark ? '#93c5fd' : '#1d4ed8', padding: 6, flexShrink: 0 }}
                    title="Open on Property Finder"
                  >
                    <IconLink />
                  </a>
                )}
              </div>
            );
          })
        )}
      </div>

      <button onClick={handleBulkAssign} disabled={saving} style={{ marginTop: 16, width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
        {saving ? 'Assigning…' : `Assign ${selectedRefs.size} Listing(s)`}
      </button>

      <ListingDetailsModal listing={viewingListing} onClose={() => setViewingListing(null)} isDark={isDark} />
    </div>
  );
};

/* ─── Main Page ───────────────────────────────────────────────────────────── */
export const PropertyFinderIntegrationPage = () => {
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const dispatch     = useDispatch();
  const sidebarWidth = useSidebarWidth();

  useEffect(() => {
    dispatch(GetAll_stage_Data());
    dispatch(GetAllchannalData());
    dispatch(GetCommunicationwayData());
    dispatch(GetAllCampainData());
    dispatch(GetallProjectsdata());
    dispatch(Get_All_Sales_Action());
  }, [dispatch]);

  const resProjects = useSelector(s => s.AllProjects.Projects);
  const ressales    = useSelector(s => s.AllSales.sales);
  const resStages   = useSelector(s => s.Allstages.stage);
  const resChannal  = useSelector(s => s.AllChannalRoot.channal);
  const resCW       = useSelector(s => s.AllCommunicationway.communicationway);
  const resCampaign = useSelector(s => s.AllCampaign.campaign);

  const Allprojects  = Array.isArray(resProjects?.data)  ? resProjects.data  : [];
  const allStages    = Array.isArray(resStages?.data)    ? resStages.data    : [];
  const allChannal   = Array.isArray(resChannal?.data)   ? resChannal.data   : [];
  const allCW        = Array.isArray(resCW?.data)        ? resCW.data        : [];
  const allCampaigns = Array.isArray(resCampaign?.data)  ? resCampaign.data  : [];

  const EXCLUDE = ['Default', 'Default M', 'Default T', 'Default S', 'Deveolper', 'Developer', 'Dev'];
  const salesData = (Array.isArray(ressales?.data) ? ressales.data : []).filter(s => !EXCLUDE.includes(s.name));

  let user = null;
  try { user = JSON.parse(localStorage.getItem('user')); } catch {}

  /* ── Mappings list ── */
  const [mappings, setMappings]       = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [drawerOpen, setDrawerOpen]   = useState(false); // ✅ حالة الـ Drawer

  /* ── Form state ── */
  const [pfProjectId, setPfProjectId]                 = useState('');
  const [pfListingReference, setPfListingReference]   = useState('');
  const [note, setNote]                               = useState('');
  const [project, setProject]                         = useState('');
  const [salesdata, setSalesdata]                     = useState('');
  const [stage, setStage]                             = useState('');
  const [channal, setChannal]                         = useState('');
  const [communicationway, setCommunicationway]       = useState('');
  const [campaignid, setCampaignid]                   = useState('');

  // ✅ Stage اتقفل تلقائي على "No Stage" — مفيش اختيار يدوي
  useEffect(() => {
    const noStage = findByName(allStages, 'No Stage');
    if (noStage) setStage(noStage._id);
  }, [allStages]);

  // ✅ Campaign اتقفل تلقائي على "Default" — مفيش اختيار يدوي
  useEffect(() => {
    const defaultCampaign = findByName(allCampaigns, 'Default', 'CampainName');
    if (defaultCampaign) setCampaignid(defaultCampaign._id);
  }, [allCampaigns]);

  // ✅ لو المستخدم مش Admin ولا Marketer، يتقفل الـ Sales عليه هو بس (disabled)
  // ✅ FIX: بنقارن userlog بتاع الـ sales record باليوزر الحالي، مش s._id
  const userRole        = user?.data?.role;
  const salesRestricted = Boolean(userRole) && userRole !== 'Admin' && userRole !== 'Marketer';
  useEffect(() => {
    if (salesRestricted) {
      const own = findOwnSalesRecord(salesData, user);
      if (own) setSalesdata(own._id);
    }
  }, [salesRestricted, salesData, user]);

  // ✅ NEW: الـ id بتاع الـ sales record الخاص باليوزر الحالي (يُستخدم لفلترة الـ mappings)
  const ownSalesId = findOwnSalesRecord(salesData, user)?._id || '';

  // ✅ NEW: لو اليوزر مش Admin/Marketer، اعرض بس الـ mappings اللي بتخصه هو
  const visibleMappings = salesRestricted
    ? mappings.filter(m => getSalesIdFromMapping(m) === ownSalesId)
    : mappings;

  const fetchMappings = async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${db_url}api/v1/pf-mapping`);
      setMappings(data.data || []);
    } catch (err) {
      Notify('error', 'Failed to load mappings', 'error');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { fetchMappings(); }, []);

  const resetForm = () => {
    setPfProjectId(''); setPfListingReference(''); setNote('');
    setProject(''); setSalesdata(''); setChannal(''); setCommunicationway('');
    // Stage و Campaign بيترجعوا يتظبطوا تلقائي بالـ useEffect بمجرد توفر الليستات
    setEditingId(null); setShowForm(false);
  };

  const handleSave = async () => {
    if (!pfProjectId && !pfListingReference)
      return Notify('error', 'Enter a PF Project ID or Listing Reference', 'error');
    if (!project || !salesdata || !stage || !channal || !communicationway || !campaignid)
      return Notify('error', 'All webhook fields are required (Project/Sales/Stage/Channel/Communication/Campaign)', 'error');

    const params = {
      addedby: user?.data?._id || '', stage, Channal: channal, project, salesdata,
      Communicationway: communicationway, campaignid,
      includePhone2: 'false', includeWhatsapp: 'false', includeJobDesc: 'false',
      includeQuestion1: 'false', includeQuestion2: 'false', includeQuestion3: 'false',
      includeQuestion4: 'false', includeQuestion5: 'false',
    };
    const targetWebhookUrl = `${db_url}api/v1/users/create-with-params?${new URLSearchParams(params).toString()}`;

    const payload = {
      pfProjectId: pfProjectId || null,
      pfListingReference: pfListingReference || null,
      targetWebhookUrl,
      note,
      isActive: true,
    };

    try {
      if (editingId) {
        await axios.put(`${db_url}api/v1/pf-mapping/${editingId}`, payload);
        Notify('success', 'Mapping updated', 'success');
      } else {
        await axios.post(`${db_url}api/v1/pf-mapping`, payload);
        Notify('success', 'Mapping created successfully', 'success');
      }
      resetForm();
      fetchMappings();
    } catch (err) {
      Notify('error', err.response?.data?.message || 'Something went wrong', 'error');
    }
  };

  const handleEdit = (m) => {
    setPfProjectId(m.pfProjectId || '');
    setPfListingReference(m.pfListingReference || '');
    setNote(m.note || '');
    setEditingId(m._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await axios.delete(`${db_url}api/v1/pf-mapping/${id}`);
      Notify('success', 'Deleted', 'success');
      fetchMappings();
    } catch {
      Notify('error', 'Delete failed', 'error');
    }
  };

  const toggleActive = async (m) => {
    try {
      await axios.put(`${db_url}api/v1/pf-mapping/${m._id}`, { isActive: !m.isActive });
      fetchMappings();
    } catch {
      Notify('error', 'Update failed', 'error');
    }
  };

  return (
    <div style={{ position: 'fixed', top: '50px', left: sidebarWidth, right: 0, bottom: 0, transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)', overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Breadcrumb */}
      <section style={{ padding: '16px 20px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <Link to="/AllLeadsForAdmin" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Home</Link>
          <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
          <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Property Finder</span>
        </div>
      </section>

      <section style={{ flex: 1, padding: '20px 20px 48px' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#7c1d1d,#dc2626)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}><IconPF /></div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: isDark ? '#6b7280' : '#94a3b8' }}>INTEGRATIONS</div>
              <h1 style={{ fontSize: '22px', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', margin: 0, letterSpacing: '-0.4px' }}>Property Finder Integration</h1>
            </div>
          </div>

          {/* ✅ زرار فتح الـ Drawer */}
          <button
            onClick={() => setDrawerOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(29,78,216,0.35)' }}
          >
            <IconLayers /> Bulk Assign Listings
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,480px) 1fr', gap: '24px', alignItems: 'start' }}>

          {/* ══ LEFT — Form ══ */}
          <div style={{ backgroundColor: isDark ? '#1e2132' : '#fff', borderRadius: '16px', padding: '24px', border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0', boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)' }}>

            {!showForm ? (
              <button onClick={() => setShowForm(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(29,78,216,0.35)' }}>
                <IconPlus /> Add New Mapping
              </button>
            ) : (
              <>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 18, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                  {editingId ? 'Edit Mapping' : 'New Mapping'}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div>
                    <label style={labelSt(isDark)}>PF Project ID (Primary Plus)</label>
                    <input style={inputSt(isDark)} value={pfProjectId} onChange={e => setPfProjectId(e.target.value)} placeholder="PF project UUID" />
                  </div>
                  <div>
                    <label style={labelSt(isDark)}>PF Listing Reference</label>
                    <input style={inputSt(isDark)} value={pfListingReference} onChange={e => setPfListingReference(e.target.value)} placeholder="Listing reference" />
                  </div>
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#6b7280' : '#94a3b8', textTransform: 'uppercase', margin: '14px 0 10px', letterSpacing: '0.08em' }}>
                  Lead Settings for This CRM
                </div>

                {/* Stage & Campaign اتشالوا من هنا وبيتحطوا تلقائي (No Stage / Default) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div><label style={labelSt(isDark)}>Project *</label><SingleSelect label="Choose project…" options={Allprojects} value={project} onChange={setProject} isDark={isDark} getLabel={o => o.name || ''} getId={o => o._id} required /></div>
                  <div><label style={labelSt(isDark)}>Sales *</label><SingleSelect label="Choose sales…" options={salesData} value={salesdata} onChange={setSalesdata} isDark={isDark} getLabel={o => o.name || ''} getId={o => o._id} required disabled={salesRestricted} /></div>
                  <div><label style={labelSt(isDark)}>Channel *</label><SingleSelect label="Choose channel…" options={allChannal} value={channal} onChange={setChannal} isDark={isDark} getLabel={o => o.name || ''} getId={o => o._id} required /></div>
                  <div><label style={labelSt(isDark)}>Communication Way *</label><SingleSelect label="Choose communication way…" options={allCW} value={communicationway} onChange={setCommunicationway} isDark={isDark} getLabel={o => o.name || ''} getId={o => o._id} required /></div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelSt(isDark)}>Note</label>
                  <input style={inputSt(isDark)} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Apartments General - PF" />
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleSave} style={{ flex: 1, padding: '12px 18px', borderRadius: 10, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    {editingId ? 'Update' : 'Save'}
                  </button>
                  <button onClick={resetForm} style={{ padding: '12px 18px', borderRadius: 10, border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`, background: 'transparent', color: isDark ? '#9ca3af' : '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ══ RIGHT — List ══ */}
          <div style={{ backgroundColor: isDark ? '#1e2132' : '#fff', borderRadius: '16px', padding: '24px', border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0', boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: isDark ? '#f1f5f9' : '#0f172a' }}>
              Current Mappings ({visibleMappings.length})
            </div>

            {loadingList ? (
              <p style={{ fontSize: 13, color: isDark ? '#6b7280' : '#94a3b8' }}>Loading…</p>
            ) : visibleMappings.length === 0 ? (
              <p style={{ fontSize: 13, color: isDark ? '#6b7280' : '#94a3b8' }}>No mappings registered yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {visibleMappings.map(m => (
                  <div key={m._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 12, border: `1.5px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`, backgroundColor: isDark ? '#151827' : '#f8fafc' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', wordBreak: 'break-all' }}>
                        {m.pfProjectId ? `Project: ${m.pfProjectId}` : `Listing: ${m.pfListingReference}`}
                      </div>
                      <div style={{ fontSize: 12, color: isDark ? '#6b7280' : '#94a3b8', marginTop: 3 }}>{m.note || '—'}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 12 }}>
                      <span onClick={() => toggleActive(m)} style={{ cursor: 'pointer', padding: '5px 11px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: m.isActive ? '#16a34a22' : '#94a3b822', color: m.isActive ? '#16a34a' : '#94a3b8', whiteSpace: 'nowrap' }}>
                        {m.isActive ? '✓ Active' : '⛔ Paused'}
                      </span>
                      <span onClick={() => handleEdit(m)} style={{ cursor: 'pointer', color: isDark ? '#93c5fd' : '#1d4ed8', display: 'flex' }}><IconEdit /></span>
                      <span onClick={() => handleDelete(m._id)} style={{ cursor: 'pointer', color: '#ef4444', display: 'flex' }}><IconTrash /></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ✅ Bulk Assign جوه Drawer من اليمين */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Bulk Assign Listings" isDark={isDark}>
        <BulkAssignPanel
          isDark={isDark}
          Allprojects={Allprojects}
          salesData={salesData}
          allStages={allStages}
          allChannal={allChannal}
          allCW={allCW}
          allCampaigns={allCampaigns}
          user={user}
          onDone={() => { fetchMappings(); setDrawerOpen(false); }}
        />
      </Drawer>

      <footer style={{ flexShrink: 0, padding: '14px 20px', borderTop: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`, fontSize: '12px', color: isDark ? '#4b5563' : '#9ca3af', fontFamily: "'DM Sans', sans-serif" }}>
        <span>© 2026 EstateCore CRM. Smart CRM that connects customers, data, and sales in one safe place.</span>
      </footer>
    </div>
  );
};
